<template>
  <Card>
    <CardHeader>
      <CardTitle>Referrals</CardTitle>
    </CardHeader>
    <CardContent>
      <div v-if="contact.referralLink" class="mb-5">
        <p class="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Referral Link</p>
        <div class="flex items-stretch gap-2">
          <div
            class="min-w-0 flex-1 truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
          >
            {{ contact.referralLink }}
          </div>
          <Button variant="outline" class="shrink-0" aria-label="Copy referral link" @click="copyLink">
            <Check v-if="copied" class="h-4 w-4" />
            <Copy v-else class="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ul v-if="contact.referrals.length > 0" role="list" class="-mx-3 divide-y divide-gray-100">
        <li v-for="referral in contact.referrals" :key="referral.id">
          <router-link
            :to="`/contacts/${referral.id}`"
            class="group flex items-center justify-between gap-3 rounded-md px-3 py-3 transition-colors hover:bg-gray-50"
          >
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-gray-900 transition-colors group-hover:text-indigo-600">
                {{ referral.name }}
              </p>
              <p class="mt-0.5 text-xs text-gray-500">
                {{ formatDate(referral.created_at, 'MMMM dd yyyy, h:mm a') }}
              </p>
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              <ReferralSourceBadge :source-kind="referral.source_kind" />
              <ContactStageBadge :stage="referral.stage" />
            </div>
          </router-link>
        </li>
      </ul>

      <div v-else class="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        No referrals yet. Referrals made with this teammate's link, or added manually, will show up here.
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Copy } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import DateUtility from '@/utilities/dates';
import ReferralSourceBadge from '@/components/shared/ReferralSourceBadge.vue';
import ContactStageBadge from '@/components/contact/ContactStageBadge.vue';
import type { AgentReferralEntity } from '@/api/entity_types/AgentReferral';

const props = defineProps<{
  contact: { referralLink?: string | null; referrals: AgentReferralEntity[] };
}>();

const { formatDate } = DateUtility;

const copied = ref(false);

const copyLink = async () => {
  if (!props.contact.referralLink) return;
  try {
    await navigator.clipboard.writeText(props.contact.referralLink);
    copied.value = true;
    toast.success('Link copied to clipboard');
    setTimeout(() => (copied.value = false), 2000);
  } catch {
    toast.error('Could not copy. Try selecting the link manually.');
  }
};
</script>
